import { borderStyles, Style, width, joinHorizontal, defaultBorderStyle } from '@boba-cli/chapstick';
import { newBinding, matches } from '@boba-cli/key';
import { KeyMsg } from '@boba-cli/tea';

// src/model.ts
var defaultKeyMap = {
  lineUp: newBinding({
    keys: ["up", "k"],
    help: { key: "\u2191/k", desc: "up" }
  }),
  lineDown: newBinding({
    keys: ["down", "j"],
    help: { key: "\u2193/j", desc: "down" }
  }),
  pageUp: newBinding({
    keys: ["pgup", "b", "ctrl+u"],
    help: { key: "pgup/b", desc: "page up" }
  }),
  pageDown: newBinding({
    keys: ["pgdown", "f", "ctrl+d"],
    help: { key: "pgdn/f", desc: "page down" }
  }),
  halfPageUp: newBinding({
    keys: ["u"],
    help: { key: "u", desc: "\xBD page up" }
  }),
  halfPageDown: newBinding({
    keys: ["d"],
    help: { key: "d", desc: "\xBD page down" }
  }),
  gotoTop: newBinding({
    keys: ["home", "g"],
    help: { key: "home/g", desc: "go to start" }
  }),
  gotoBottom: newBinding({
    keys: ["end", "G"],
    help: { key: "end/G", desc: "go to end" }
  })
};
function defaultStyles() {
  return {
    header: new Style().bold(true).padding(0, 1),
    cell: new Style().padding(0, 1),
    selected: new Style().background("#7D56F4").foreground("#FFFFFF"),
    border: new Style().foreground("#383838"),
    borderStyle: borderStyles.normal
  };
}

// src/model.ts
var ELLIPSIS = "\u2026";
function clamp(value, min, max) {
  if (Number.isNaN(value)) return min;
  if (max < min) return min;
  return Math.min(max, Math.max(min, value));
}
function clampOffset(offset, rowCount, height, cursor) {
  if (height <= 0) return 0;
  const maxOffset = Math.max(0, rowCount - height);
  let nextOffset = clamp(offset, 0, maxOffset);
  if (cursor < nextOffset) {
    nextOffset = cursor;
  } else if (cursor >= nextOffset + height) {
    nextOffset = Math.max(0, cursor - height + 1);
  }
  return nextOffset;
}
function truncateWithEllipsis(text, width$1) {
  const target = Math.max(0, width$1);
  if (target === 0) return "";
  if (width(text) <= target) return text;
  if (target === 1) return ELLIPSIS;
  let acc = "";
  for (const ch of text) {
    if (width(acc + ch) >= target) {
      break;
    }
    acc += ch;
    if (width(acc) === target - 1) {
      break;
    }
  }
  return `${acc}${ELLIPSIS}`;
}
function padToWidth(input, width$1) {
  const current = width(input);
  const missing = Math.max(0, width$1 - current);
  return `${input}${" ".repeat(missing)}`;
}
function calculateColumnWidths(columns, totalWidth, bordered) {
  const borderWidth = bordered ? columns.length + 1 : 0;
  const available = Math.max(0, totalWidth - borderWidth);
  const fixed = columns.map((c) => Math.max(0, c.width));
  const totalFixed = fixed.reduce((a, b) => a + b, 0);
  if (totalFixed > available && totalFixed > 0) {
    const scale = available / totalFixed;
    return fixed.map((w) => Math.max(0, Math.floor(w * scale)));
  }
  return fixed;
}
function mergeKeyMap(base, overrides) {
  return {
    lineUp: overrides?.lineUp ?? base.lineUp,
    lineDown: overrides?.lineDown ?? base.lineDown,
    pageUp: overrides?.pageUp ?? base.pageUp,
    pageDown: overrides?.pageDown ?? base.pageDown,
    halfPageUp: overrides?.halfPageUp ?? base.halfPageUp,
    halfPageDown: overrides?.halfPageDown ?? base.halfPageDown,
    gotoTop: overrides?.gotoTop ?? base.gotoTop,
    gotoBottom: overrides?.gotoBottom ?? base.gotoBottom
  };
}
function mergeStyles(overrides, borderStyleOverride) {
  const base = defaultStyles();
  const merged = {
    ...base,
    ...overrides
  };
  if (borderStyleOverride) {
    merged.borderStyle = borderStyleOverride;
  } else if (!merged.borderStyle) {
    merged.borderStyle = defaultBorderStyle;
  }
  return merged;
}
function totalColumnWidth(columns, bordered) {
  const base = columns.reduce((sum, col) => sum + Math.max(0, col.width), 0);
  return bordered ? base + Math.max(0, columns.length - 1) : base;
}
function fixedWidthCell(text, width) {
  if (width <= 0) return "";
  const truncated = truncateWithEllipsis(text, width);
  return new Style().width(width).maxWidth(width).alignHorizontal("left").inline(true).render(truncated);
}
function wrapWithBorder(line, innerWidth, styles) {
  const width = Math.max(0, innerWidth);
  const padded = padToWidth(line, width);
  const wrapped = `${styles.borderStyle.left}${padded}${styles.borderStyle.right}`;
  return styles.border.render(wrapped);
}
function renderTopBorder(innerWidth, styles) {
  const line = `${styles.borderStyle.topLeft}${styles.borderStyle.top.repeat(Math.max(0, innerWidth))}${styles.borderStyle.topRight}`;
  return styles.border.render(line);
}
function renderSeparator(innerWidth, styles) {
  const line = `${styles.borderStyle.left}${styles.borderStyle.top.repeat(Math.max(0, innerWidth))}${styles.borderStyle.right}`;
  return styles.border.render(line);
}
function renderBottomBorder(innerWidth, styles) {
  const line = `${styles.borderStyle.bottomLeft}${styles.borderStyle.bottom.repeat(Math.max(0, innerWidth))}${styles.borderStyle.bottomRight}`;
  return styles.border.render(line);
}
var TableModel = class _TableModel {
  columns;
  rows;
  cursor;
  offset;
  focused;
  height;
  width;
  autoWidth;
  keyMap;
  styles;
  bordered;
  columnWidths;
  constructor(state) {
    this.columns = state.columns;
    this.rows = state.rows;
    this.cursor = state.cursor;
    this.offset = state.offset;
    this.focused = state.focused;
    this.height = state.height;
    this.width = state.width;
    this.autoWidth = state.autoWidth;
    this.keyMap = state.keyMap;
    this.styles = state.styles;
    this.bordered = state.bordered;
    this.columnWidths = state.columnWidths;
  }
  /** Create a new table model. */
  static new(options) {
    const rows = [...options.rows ?? []];
    const bordered = options.bordered ?? false;
    const styles = mergeStyles(options.styles, options.borderStyle);
    const width = options.width ?? totalColumnWidth(options.columns, bordered) ?? 0;
    const autoWidth = options.width === void 0;
    const height = Math.max(0, options.height ?? rows.length);
    const columnWidths = autoWidth ? options.columns.map((c) => Math.max(0, c.width)) : calculateColumnWidths(options.columns, width, bordered);
    const cursor = clamp(
      options.focused ? 0 : 0,
      0,
      Math.max(rows.length - 1, 0)
    );
    const offset = clampOffset(0, rows.length, height, cursor);
    return new _TableModel({
      columns: [...options.columns],
      rows,
      cursor,
      offset,
      focused: options.focused ?? false,
      height,
      width,
      autoWidth,
      keyMap: mergeKeyMap(defaultKeyMap, options.keyMap),
      styles,
      bordered,
      columnWidths
    });
  }
  /** Tea init hook (no-op). */
  init() {
    return null;
  }
  /** Handle key messages when focused. */
  update(msg) {
    if (!this.focused || !(msg instanceof KeyMsg)) {
      return [this, null];
    }
    if (matches(msg, this.keyMap.lineUp)) {
      return [this.moveUp(1), null];
    }
    if (matches(msg, this.keyMap.lineDown)) {
      return [this.moveDown(1), null];
    }
    if (matches(msg, this.keyMap.pageUp)) {
      return [this.moveUp(this.height || 1), null];
    }
    if (matches(msg, this.keyMap.pageDown)) {
      return [this.moveDown(this.height || 1), null];
    }
    if (matches(msg, this.keyMap.halfPageUp)) {
      return [this.moveUp(Math.max(1, Math.floor(this.height / 2))), null];
    }
    if (matches(msg, this.keyMap.halfPageDown)) {
      return [this.moveDown(Math.max(1, Math.floor(this.height / 2))), null];
    }
    if (matches(msg, this.keyMap.gotoTop)) {
      return [this.gotoTop(), null];
    }
    if (matches(msg, this.keyMap.gotoBottom)) {
      return [this.gotoBottom(), null];
    }
    return [this, null];
  }
  /** Render the header and visible rows as a string. */
  view() {
    const header = this.renderHeader();
    const headerWidth = width(header);
    const start = this.offset;
    const end = Math.min(start + this.height, this.rows.length);
    const visibleRows = this.renderAllRows().slice(start, end);
    const paddedRows = [...visibleRows];
    while (paddedRows.length < this.height) {
      paddedRows.push(this.renderEmptyRow());
    }
    const rowWidth = paddedRows.length > 0 ? width(paddedRows[0] ?? "") : 0;
    const innerWidth = Math.max(headerWidth, rowWidth);
    if (!this.bordered) {
      return [
        padToWidth(header, innerWidth),
        ...paddedRows.map((r) => padToWidth(r, innerWidth))
      ].join("\n");
    }
    const wrap = (line) => wrapWithBorder(line, innerWidth, this.styles);
    return [
      renderTopBorder(innerWidth, this.styles),
      wrap(padToWidth(header, innerWidth)),
      renderSeparator(innerWidth, this.styles),
      ...paddedRows.map((r) => wrap(padToWidth(r, innerWidth))),
      renderBottomBorder(innerWidth, this.styles)
    ].join("\n");
  }
  /** Current selected row, if any. */
  selectedRow() {
    if (this.rows.length === 0) return void 0;
    return this.rows[this.cursor];
  }
  /** Current selected index. */
  selectedIndex() {
    return this.cursor;
  }
  /** Replace columns. */
  setColumns(columns) {
    return this.with({
      columns: [...columns]
    });
  }
  /** Replace rows. */
  setRows(rows) {
    return this.with({
      rows: [...rows]
    });
  }
  /** Append a single row. */
  appendRow(row) {
    return this.setRows([...this.rows, row]);
  }
  /** Remove a row by index. */
  removeRow(index) {
    if (index < 0 || index >= this.rows.length) return this;
    const next = [...this.rows];
    next.splice(index, 1);
    return this.setRows(next);
  }
  /** Set the selected index (clamped). */
  setSelectedIndex(index) {
    return this.with({ cursor: index });
  }
  /** Move the cursor up by n rows. */
  moveUp(n = 1) {
    const delta = Math.max(1, n);
    return this.with({ cursor: this.cursor - delta });
  }
  /** Move the cursor down by n rows. */
  moveDown(n = 1) {
    const delta = Math.max(1, n);
    return this.with({ cursor: this.cursor + delta });
  }
  /** Jump to the first row. */
  gotoTop() {
    return this.with({ cursor: 0 });
  }
  /** Jump to the last row. */
  gotoBottom() {
    return this.with({ cursor: this.rows.length - 1 });
  }
  /** Focus the table. */
  focus() {
    if (this.focused) return this;
    return this.with({ focused: true });
  }
  /** Blur the table. */
  blur() {
    if (!this.focused) return this;
    return this.with({ focused: false });
  }
  /** Set the visible height (rows). */
  setHeight(height) {
    return this.with({ height });
  }
  /** Set the total width. */
  setWidth(width) {
    return this.with({ width });
  }
  renderHeader() {
    const cells = this.columns.map((col, i) => {
      const width = this.columnWidths[i] ?? col.width;
      const fixedCell = fixedWidthCell(col.title, width);
      return this.styles.header.render(fixedCell);
    });
    return joinHorizontal(...cells);
  }
  renderRow(row, index) {
    const isSelected = this.focused && index === this.cursor;
    const cells = this.columns.map((col, i) => {
      const width = this.columnWidths[i] ?? col.width;
      const value = row[i] ?? "";
      const fixedCell = fixedWidthCell(value, width);
      return this.styles.cell.render(fixedCell);
    });
    const joined = joinHorizontal(...cells);
    if (isSelected) {
      return this.styles.selected.render(joined);
    }
    return joined;
  }
  renderEmptyRow() {
    const cells = this.columns.map((col, i) => {
      const width = this.columnWidths[i] ?? col.width;
      const fixedCell = fixedWidthCell("", width);
      return this.styles.cell.render(fixedCell);
    });
    return joinHorizontal(...cells);
  }
  renderAllRows() {
    return this.rows.map((row, i) => this.renderRow(row, i));
  }
  with(patch) {
    const nextColumns = patch.columns ?? this.columns;
    const nextRows = patch.rows ?? this.rows;
    const nextHeight = patch.height ?? this.height;
    const nextWidth = patch.width ?? this.width;
    const autoWidth = patch.width !== void 0 ? false : this.autoWidth;
    const nextBordered = patch.bordered ?? this.bordered;
    const nextColumnWidths = autoWidth ? nextColumns.map((c) => Math.max(0, c.width)) : calculateColumnWidths(nextColumns, nextWidth, nextBordered);
    const cursor = clamp(
      patch.cursor ?? this.cursor,
      0,
      Math.max(nextRows.length - 1, 0)
    );
    const offset = clampOffset(
      patch.offset ?? this.offset,
      nextRows.length,
      nextHeight,
      cursor
    );
    return new _TableModel({
      columns: [...nextColumns],
      rows: [...nextRows],
      cursor,
      offset,
      focused: patch.focused ?? this.focused,
      height: Math.max(0, nextHeight),
      width: Math.max(0, nextWidth),
      autoWidth,
      keyMap: patch.keyMap ?? this.keyMap,
      styles: patch.styles ?? this.styles,
      bordered: nextBordered,
      columnWidths: nextColumnWidths
    });
  }
};

export { TableModel, defaultKeyMap, defaultStyles };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map